import React, { useState, useEffect, useRef } from "react";
import { Card } from "antd";
import { DataTable, SearchBox } from "./components";
import http from "../../../../services/http";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import SubscriptionModal from "../../../../components/subscription-modal";
import FreeTrialBanner from "../../../../components/free-trial-banner";
import { toast } from "react-toastify";
import { setProfile } from "../../../../store/actions/user";
import {
  ENTERPRISE_ROLE,
  privateEquityTypeFields,
  recordActions,
  ventureTypeFields,
} from "../../../../constant";
import { salesforceOauth } from "../../../../utils";
import "./styles.scss";

function Dataset() {
  // STATES
  const [initLoad, setInitLoad] = useState(true);
  const [isLoading, setLoading] = useState(false);
  const [searchVal, setSearchVal] = useState("");
  const [selectedFilters, setselectedFilters] = useState({
    type_filter: "default",
  });
  const [viewAbleFilters, setViewAbleFilters] = useState({});
  const [selectedSorter, setselectedSorter] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [advancedSearch, setAdvancedSearch] = useState(false);
  const [investors, setInvestors] = useState([]);
  const [subscriptionPreview, setSubscriptionPreview] = useState(false);
  const [isCrmExportLoding, setCrmExportLoding] = useState(false);

  // REDUX DATA
  const user = useSelector((state) => state.UserReducer.profile);

  // CONST VALS
  const latestRequestIdRef = useRef(0);
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const exportToCRM = searchParams.get("connected");
  const dispatch = useDispatch();
  const DATA_LIMIT = 10;
  const savedFilters = JSON.parse(localStorage.getItem("selected_filters"));
  const viewableFilters = JSON.parse(localStorage.getItem("viewable_filters"));
  const isEnterpriseUser = user?.role === ENTERPRISE_ROLE;
  const searchAccess =
    isEnterpriseUser ||
    user?.is_subscribed ||
    (user?.remaining_trial > 0 && user?.free_trial);

  // HELPERS
  const sortHandler = (sorter) => {
    if (!sorter.order) {
      setselectedSorter(null);
      return;
    }
    if (!sorter.columnKey) return;
    let sortingoption = sorter.order === "descend" ? "-" : "";
    let selectedSorter = sortingoption + sorter.columnKey;
    setselectedSorter(selectedSorter);
  };

  const getInvestorsForPDF = async () => {
    const appliedFilter = { ...selectedFilters };
    Object.keys(appliedFilter).forEach((key) => {
      if (appliedFilter[key] === undefined || appliedFilter[key]?.length === 0)
        delete appliedFilter[key];
    });
    const params = {
      ...appliedFilter,
      offset: 0,
      // TODO: uncomment this when we restrict professional users from accessing enterprise features
      // limit: isEnterpriseUser ? 300 : 100,
      limit: 300,
    };
    if (selectedSorter) params.sort = selectedSorter;
    const queryparams = new URLSearchParams(params).toString();
    const { data } = await http.get(`dataset?${queryparams}`);
    return data?.data;
  };

  const exportInvestorsToCRM = async (filters) => {
    setCrmExportLoding(true);
    const appliedFilter = filters ?? { ...selectedFilters };
    Object.keys(appliedFilter).forEach((key) => {
      if (appliedFilter[key] === undefined || appliedFilter[key]?.length === 0)
        delete appliedFilter[key];
    });
    const params = {
      ...appliedFilter,
      offset: 0,
      limit: 200,
      crm_export: true,
    };
    if (selectedSorter) params.sort = selectedSorter;
    const queryparams = new URLSearchParams(params).toString();
    http
      .get(`dataset?${queryparams}`)
      .then(({ data }) => {
        toast.success("Investors exported to CRM successfully");
        http.post("action-record", {
          action: recordActions.investorCRMExport,
        });
      })
      .finally(() => {
        setCrmExportLoding(false);
        searchParams.delete("connected");
        window.history.replaceState(null, "", window.location.pathname);
      })
      .catch((err) => {
        const isUnauthorized = err?.response?.status === 403;
        if (isUnauthorized) {
          salesforceOauth();
          return;
        }
        toast.error(err?.response?.data?.error ?? "Failed to export to CRM");
      });
  };

  const getInvestorsHelper = async (fromFilter) => {
    if (!searchAccess) return;
    // Increment request counter to identify this request
    const currentRequestId = ++latestRequestIdRef.current;

    setLoading(true);
    const appliedFilter = { ...selectedFilters };

    Object.keys(appliedFilter).forEach((key) => {
      if (!appliedFilter[key] || appliedFilter[key]?.length === 0) {
        delete appliedFilter[key];
      }
    });

    localStorage.setItem("selected_filters", JSON.stringify(appliedFilter));
    localStorage.setItem("viewable_filters", JSON.stringify(viewAbleFilters));

    const currentPage = fromFilter ? 1 : page;

    const params = {
      ...appliedFilter,
      offset: (currentPage - 1) * DATA_LIMIT,
      limit: DATA_LIMIT,
      ...(selectedSorter && { sort: selectedSorter }),
      advanced_search: advancedSearch,
    };
    try {
      const { data } = await http.get(`dataset?${new URLSearchParams(params)}`);

      // Only update state if this is still the latest request
      if (currentRequestId === latestRequestIdRef.current) {
        setInvestors(data?.data);
        setTotal(data?.total);
        dispatch(
          setProfile({
            ...user,
            remaining_trial: data?.remaining_trial,
          })
        );
        setPage(currentPage);
      }
    } catch ({ response }) {
      // Only show error for the latest request
      if (currentRequestId === latestRequestIdRef.current) {
        const error =
          response?.data?.error ?? response?.data?.detail ?? "Something went wrong";
        toast.error(error);
      }
    } finally {
      // Only update loading state if this is still the latest request
      if (currentRequestId === latestRequestIdRef.current) {
        setAdvancedSearch(false);
        setLoading(false);
      }
    }
  };

  // HANDLERS
  const removeFilterHandler = (key) => {
    // remove from selected filters
    const newFilters = { ...selectedFilters };
    const newViewAbleFilters = { ...viewAbleFilters };

    delete newFilters[key];
    delete newViewAbleFilters[key];

    if (key === "type") {
      [...ventureTypeFields, ...privateEquityTypeFields].forEach((field) => {
        delete newFilters[field];
        delete newViewAbleFilters[field];
      });
    }
    setselectedFilters(newFilters);
    setViewAbleFilters(newViewAbleFilters);
  };

  // HOOKS
  useEffect(() => {
    if (initLoad) return;
    getInvestorsHelper();
  }, [page, selectedSorter]);

  useEffect(() => {
    if (initLoad) return;
    if (isLoading) return;
    getInvestorsHelper(true);
  }, [selectedFilters]);

  useEffect(() => {
    if (!initLoad) return;
    setInitLoad(false);
    if (savedFilters) {
      setselectedFilters({...savedFilters, type_filter: savedFilters?.type_filter || "default"});
      if (savedFilters?.description) setSearchVal(savedFilters?.description);
    } else {
      setselectedFilters({ type_filter: "default" });
      setSearchVal("");
    }
    if (exportToCRM) {
      setCrmExportLoding(true);
      exportInvestorsToCRM(savedFilters);
    }
  }, [savedFilters, exportToCRM]);

  useEffect(() => {
    if (!initLoad) return;
    setInitLoad(false);
    setViewAbleFilters(viewableFilters ?? {});
  }, [viewableFilters]);

  return (
    <>
      {user?.free_trial && !isEnterpriseUser && (
        <FreeTrialBanner
          title={
            user?.remaining_trial === 0
              ? "Your free trial has ended!"
              : "Enjoy your free trial!"
          }
          description={
            user?.remaining_trial === 0 ? (
              "You've used all your free searches. Upgrade now to unlock unlimited access."
            ) : user?.remaining_trial === 1 ? (
              "This is your last free search. Upgrade now to keep exploring investors."
            ) : (
              <>
                You have <strong>{user?.remaining_trial} free searches</strong> left—make
                the most of them!
              </>
            )
          }
          remainingCount={user?.remaining_trial ?? 0}
          maxCount={5}
          onUpgrade={() => setSubscriptionPreview(true)}
        />
      )}
      <h2 className="page-title">Investors</h2>
      <Card className="dataset-wrapper">
        <SearchBox
          data={investors}
          searchAccess={searchAccess}
          searchVal={searchVal}
          setSearchVal={setSearchVal}
          getInvestorsForPDF={getInvestorsForPDF}
          selectedFilter={selectedFilters}
          setFilters={setselectedFilters}
          setViewAbleFilters={setViewAbleFilters}
          setAdvancedSearch={setAdvancedSearch}
          isCrmExportLoding={isCrmExportLoding}
          exportInvestorsToCRM={exportInvestorsToCRM}
        />
        <DataTable
          isLoading={isLoading}
          appliedFilters={viewAbleFilters}
          removeFilterHandler={removeFilterHandler}
          data={investors}
          total={total}
          page={page}
          setPage={setPage}
          sortHandler={sortHandler}
        />
      </Card>
      <SubscriptionModal
        preview={subscriptionPreview}
        closeHandler={() => setSubscriptionPreview(false)}
      />
    </>
  );
}

export default Dataset;
